import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import React, { CSSProperties, useContext } from 'react'
import clsx from 'clsx'
import { CSS } from '@dnd-kit/utilities'
import { Controller } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'
import Select from 'react-select'
import {
  closestCorners,
  DndContext,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core/dist/types'
import {
  Control,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form/dist/types/form'

import Input from '../../../common/inputs/Input'
import AmountInput from '../../../common/inputs/AmountInput'
import { SortableOverlay } from './SortableOverlay'

import { HandleModalContext } from '../../../../layout/mainlayout/MainLayout'

import MessageHelperComp from '../../../../helpers/MessageHelper'
import {
  calculatePriceWithPercent,
  getFloatPrice,
  removeLeadingZeros,
} from '../../../../helpers/price'
import { getAllRegistrationFees } from '../../../../helpers/registrationFees'
import { cn } from '../../../../lib/utils'

import { useAppSelector } from '../../../../store/hooks'
import { selectedEvent } from '../../../../store/events/eventsSlice'

import { ModelBaseModel } from '../../../../models/model-base/model-base.model'
import {
  IEventFees,
  RegistrationFeesType,
} from '../../../../models/event-fees/event-fees.interface'

import { MODAL_CONSTS } from '../../../../const/modal-const'
import { getCurrency } from '../../../../helpers/payment'

interface EventEntriesFormRowProps {
  index: number
  type?: 'children'
  field: RegistrationFeesType
  openAddNotesModal: (e: HTMLInputElement) => void
  selector: 'registrationFees' | `registrationFees.${number}.children`
  control: Control<IEventFees>
  register: UseFormRegister<IEventFees>
  watch: any
  showErrors: boolean
  errors: any
  getValues: UseFormGetValues<IEventFees>
  setValue: UseFormSetValue<IEventFees>
  dataIncrementAttrProps: { [key: string]: number }
  handleDragEnd: (event: DragEndEvent) => void
  handleDragStart: (event: DragEndEvent) => void
  activeId: string | null
}

export const EventEntriesFormRow: React.FC<EventEntriesFormRowProps> = ({
  field,
  register,
  index,
  watch,
  showErrors,
  errors,
  openAddNotesModal,
  control,
  selector,
  type,
  getValues,
  setValue,
  dataIncrementAttrProps,
  handleDragEnd,
  handleDragStart,
  activeId,
}) => {
  const handleModal = useContext(HandleModalContext)?.handleModal

  const selectedEventR = useAppSelector(selectedEvent)
  const currency = getCurrency(selectedEventR.EventPaymentSettings?.currency)

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.uuid,
  })

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const fee = dataIncrementAttrProps['data-increment-by']
  const getPriceWitPercent = () => {
    const price = calculatePriceWithPercent(watch(`${selector}.${index}.actualCost`), +fee || 0)

    // if (watch(`${selector}.${index}`)?.children?.length > 0 && !+fee) {
    //   return 'division fee'
    // }
    // if (!+fee) {
    //   return 'entry fee'
    // }
    return '• ' + getFloatPrice(`${currency}${price}`)
  }

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
  }

  const valid = !!(
    !!watch(`${selector}.${index}.name`) &&
    watch(`${selector}.${index}.note`) &&
    new ModelBaseModel().getNum(watch(`${selector}.${index}.actualCost`))
  )

  const qualifyingOptions = watch('qualifyingClasses.items')
    ?.filter((option: { name: string; price: string }) => option?.name && option?.price)
    ?.map((option: { name: string }) => ({
      label: option.name,
      value: option.name,
    }))

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'mt-4 w-full relative border border-SeabiscuitLightThemeColorD3 rounded-lg flex flex-wrap items-center justify-between pb-3 px-3 md:pl-10',
        watch(`${selector}.${index}`)?.children?.[0] ? 'pt-4' : 'pt-3',
        valid ? 'bg-SeabiscuitGrayThemeColor' : 'bg-white border-SeabiscuitLightThemeColorD3',
        isDragging && 'bg-blue-50 h-[58px] border-dashed overflow-hidden [&>*]:hidden'
      )}
    >
      <div
        {...attributes}
        {...listeners}
        className="drag-handle hidden md:flex cursor-pointer opacity-50 hover:opacity-100 w-11 absolute left-0 p-3 top-1.5 transition-all"
      >
        <img src="/assets/cp_icons/Move.svg" alt="moveIcon" className="w-full" />
      </div>
      <div className="relative w-full sm:w-[97%] lg:w-[20%]">
        <label>
          <Input
            className="p-0 border-none bg-transparent"
            register={register}
            placeholder={
              watch(`${selector}.${index}`)?.children?.[0]
                ? 'Enter division name...'
                : 'Enter class name...'
            }
            name={`${selector}.${index}.name`}
            value={watch(`${selector}.${index}.name`)}
          />
        </label>
        {showErrors && !!errors?.[index]?.name?.message ? (
          <MessageHelperComp
            className="absolute z-0 bottom-1 left-0 translate-y-full"
            isError={true}
            message={errors?.[index]?.name?.message}
          />
        ) : null}
      </div>
      <div className="w-full sm:w-[24%] lg:w-[15%]">
        {!watch(`${selector}.${index}`)?.children?.length && (
          <label className="flex items-center justify-end gap-1">
            <Input
              type="number"
              containerClassName="!w-[50px]"
              className="p-0 border-none bg-transparent text-right"
              register={register}
              placeholder="0"
              name={`${selector}.${index}.order`}
              value={watch(`${selector}.${index}.order`)}
            />
            <span className="text-SeabiscuitDark200ThemeColor/50 whitespace-nowrap text-[14px]">
              class #
            </span>
          </label>
        )}
      </div>
      <div className="relative w-full sm:w-[29%] lg:w-[18%]">
        {!watch(`${selector}.${index}`)?.children?.length && (
          <label className="flex items-center gap-1">
            <AmountInput
              name={`${selector}.${index}.actualCost`}
              placeholder={watch(`${selector}.${index}.actualCost`)}
              value={watch(`${selector}.${index}.actualCost`)}
              inputClassName="text-right p-0 w-full bg-transparent text-SeabiscuitDark200ThemeColor"
              className="text-[14px] w-full rounded-md flex items-center"
              onChange={(value: string) => {
                setValue(`${selector}.${index}.actualCost`, removeLeadingZeros(value))
              }}
            />
            <span
              className="text-SeabiscuitDark200ThemeColor/50 whitespace-nowrap text-[14px]"
              {...dataIncrementAttrProps}
            >
              {getPriceWitPercent()}
            </span>
          </label>
        )}
      </div>

      <div className="relative w-full sm:w-[38%] lg:w-[20%] flex justify-end text-right">
        <Controller
          control={control}
          name={`${selector}.${index}.qualifyingClass`}
          render={({ field: { value } }) => (
            <Select
              isDisabled={!!watch(`${selector}.${index}`)?.children?.length}
              className={clsx(
                'w-full p-0 bg-transparent border-none transition-all hover:opacity-70 text-right',
                !!watch(`${selector}.${index}`)?.children?.length && 'hidden'
              )}
              classNamePrefix="entries-select"
              isClearable={false}
              isSearchable={false}
              placeholder="Bonus fee?"
              menuPlacement="auto"
              menuPortalTarget={document.body}
              options={qualifyingOptions?.length > 0 ? qualifyingOptions : []}
              value={value?.name ? { label: value.name, value: value.name } : null}
              onChange={(newValue) => {
                if (newValue) {
                  const price = watch('qualifyingClasses.items').find(
                    (qualify: { name: string; price: number }) => qualify.name === newValue.value
                  )
                  setValue(`${selector}.${index}.qualifyingClass`, {
                    name: newValue.label,
                    price: price?.price ?? '0',
                  })
                }
              }}
              styles={{
                menu: (provided) => ({
                  ...provided,
                  textAlign: 'right',
                }),
              }}
            />
          )}
        />
      </div>
      <div className="w-full sm:w-[100%] lg:w-[17%]">
        <label className="text-[14px] flex items-center">
          <input
            type="text"
            className="text-right p-0 underline bg-transparent border-none text-[14px] text-SeabiscuitDark200ThemeColor w-full rounded-md flex items-center cursor-pointer hover:no-underline overflow-hidden whitespace-nowrap truncate"
            placeholder="Description"
            value={watch(`${selector}.${index}.note`)}
            onClick={(e) => openAddNotesModal(e.target as HTMLInputElement)}
            readOnly={true}
            {...register(`${selector}.${index}.note`)}
          />
        </label>
      </div>
      <button
        className={clsx(
          'cursor-pointer absolute top-3 translate-x-full flex items-center w-6',
          type === 'children' ? '-right-4' : '-right-1'
        )}
        onClick={() => {
          handleModal?.(true, MODAL_CONSTS.REMOVE_CONFIRMATION, {
            removeItem: function () {
              const currentItems = getValues(selector)
              const newItems = currentItems?.filter((_, i) => i !== index)
              setValue(selector, newItems)
            },
          })
        }}
      >
        <img className="w-full" src="/assets/og_icons/Cancel.svg" alt="Cancel" />
      </button>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {watch(`${selector}.${index}`).children?.length > 0 && (
          <>
            <SortableContext
              items={watch(`${selector}.${index}`).children?.map(
                ({ uuid }: RegistrationFeesType) => uuid
              )}
              strategy={verticalListSortingStrategy}
            >
              {watch(`${selector}.${index}`).children.map(
                (fieldChildren: any, childrenIndex: number) => {
                  return (
                    <EventEntriesFormRow
                      key={fieldChildren.uuid}
                      type="children"
                      field={fieldChildren}
                      register={register}
                      index={childrenIndex}
                      watch={watch}
                      showErrors={showErrors}
                      errors={errors?.[index]?.children}
                      openAddNotesModal={openAddNotesModal}
                      control={control}
                      selector={`registrationFees.${index}.children`}
                      getValues={getValues}
                      setValue={setValue}
                      dataIncrementAttrProps={dataIncrementAttrProps}
                      handleDragStart={handleDragStart}
                      handleDragEnd={handleDragEnd}
                      activeId={activeId}
                    />
                  )
                }
              )}
            </SortableContext>
            <SortableOverlay
              activeId={activeId}
              fees={getAllRegistrationFees(watch('registrationFees') || [])}
            />
          </>
        )}
      </DndContext>
      {type !== 'children' && watch(`${selector}.${index}.children`)?.length > 0 && (
        <div className="mt-4 text-[14px] text-SeabiscuitDark200ThemeColor/50 flex items-center gap-2">
          <img src={`/assets/og_icons/Cancel.svg`} className="rotate-45 w-5" alt="plus icon" />
          <button
            type="button"
            className="underline hover:no-underline"
            onClick={() => {
              setValue(`registrationFees.${index}.children`, [
                ...watch(`registrationFees.${index}.children`),
                {
                  order: null,
                  name: '',
                  category: 'registrations',
                  note: '',
                  cost: 0,
                  actualCost: 0,
                  uuid: uuidv4(),
                  increment: '',
                  qualifyingClass: '',
                  startTimeHours: '00',
                  startTimeMinutes: '00',
                  startTimeFormat: 'AM',
                },
              ])
            }}
          >
            Add class
          </button>
        </div>
      )}
    </div>
  )
}
